import React from "react";
import { inject, observer } from "mobx-react";
import { IReactionDisposer, reaction } from "mobx";
import { throttle } from "lodash";
import classNames from "classnames";
import { DocumentDndContext } from "./document-dnd-context";
import { BaseComponent, IBaseProps } from "../base";
import { kDragResizeRowId, extractDragResizeRowId, extractDragResizeY,
        extractDragResizeModelHeight, extractDragResizeDomHeight, TileRowHandle } from "../document/tile-row";
import { DocumentContentModelType } from "../../models/document/document-content";
import { IDragToolCreateInfo, IDragTilesData } from "../../models/document/document-content-types";
import { getDocumentIdentifier } from "../../models/document/document-utils";
import { IDropRowInfo } from "../../models/document/tile-row";
import { logDataTransfer } from "../../models/document/drag-tiles";
import { TileApiInterfaceContext } from "../tiles/tile-api";
import { dragTileSrcDocId, kDragTileCreate, kDragTiles } from "../tiles/tile-component";
import { safeJsonParse } from "../../utilities/js-utils";
import { RowListComponent } from "./row-list";
import { DropRowContext } from "./drop-row-context";
import { RowRefsContext } from "./row-refs-context";
import { ContainerContext } from "./container-context";

import "./document-content.scss";

interface IProps extends IBaseProps {
  content?: DocumentContentModelType;
  context: string;
  documentId?: string;
  onScroll?: (x: number, y: number) => void;
  readOnly?: boolean;
  scale?: number;
  selectedSectionId?: string | null;
  showPlaybackSpacer?: boolean;
  typeClass: string;
  viaTeacherDashboard?: boolean;
}

interface IDragResizeRow {
  id: string;
  modelHeight?: number;
  domHeight?: number;
  deltaHeight: number;
}

interface IState {
  dragResizeRow?: IDragResizeRow;
  dropRowInfo?: IDropRowInfo;
}
// Interval in ms between recalculation for highlighting drag/drop zones
const kDragUpdateInterval = 50;

@inject("stores")
@observer
export class DocumentContentComponent extends BaseComponent<IProps, IState> {

  static contextType = TileApiInterfaceContext;
  declare context: React.ContextType<typeof TileApiInterfaceContext>;

  public state: IState = {};

  private domElement: HTMLElement | null;
  private rowRefs: Array<TileRowHandle>;
  private mutationObserver: MutationObserver;
  private scrollDisposer: IReactionDisposer;
  private pickUpReactionDisposer: IReactionDisposer;

  constructor(props: IProps) {
    super(props);
    this.rowRefs = [];
    this.domElement = null;
  }

  private addRowRef = (ref: TileRowHandle) => {
    this.rowRefs.push(ref);
  };

  public componentDidMount() {
    if (this.domElement) {
      this.domElement.addEventListener("scroll", throttle(this.updateVisibleRows, 100));
      this.updateVisibleRows();
      this.scrollToSection(this.props.selectedSectionId);

      // We pass the domElement to our children, but it's undefined during the first render,
      // so we force an update to make sure we draw at least once after we have our domElement.
      this.forceUpdate();

      // TODO: scrollTo would be better if it was set on something specific to the document
      // since it is global all DocumentContentComponents are listening to this same global
      // property. So when there are lots of thumbnails then each of them will react to a
      // change of this global. It could be a volatile prop on the document model. Or the ui
      // store could have a scrollToMap with keys of the docId and values of the tileId
      // Enable mousemove-based drop zone highlighting and keyboard navigation when a tile is picked up.
      // Only the DocumentContentComponent that owns the picked-up tile registers the global keydown
      // listener. This prevents duplicate key processing in multi-pane views (2-up, 4-up).
      this.pickUpReactionDisposer = reaction(
        () => this.stores.ui.pickedUpTileId,
        (pickedUpTileId) => {
          const isOwner = pickedUpTileId
            && this.stores.ui.pickedUpDocId === this.props.content?.contentId;
          if (isOwner) {
            this.domElement?.addEventListener("mousemove", this.handlePickUpMouseMove);
            this.domElement?.addEventListener("mouseleave", this.handlePickUpMouseLeave);
            document.addEventListener("keydown", this.handlePickUpKeyDown);
          } else {
            this.domElement?.removeEventListener("mousemove", this.handlePickUpMouseMove);
            this.domElement?.removeEventListener("mouseleave", this.handlePickUpMouseLeave);
            document.removeEventListener("keydown", this.handlePickUpKeyDown);
            this.clearDropRowInfo();
          }
        }
      );

      this.scrollDisposer = reaction(
        () => {
          const docId = this.stores.ui.scrollTo?.docId;
          return getDocumentIdentifier(this.props.content) === docId
            ? this.stores.ui.scrollTo?.tileId
            : undefined;
        },
        (scrollToTileId: string | undefined) => {
          if (scrollToTileId) {
            this.rowRefs.forEach((row: TileRowHandle | null) => {
              if (row?.tileRowDiv && row.hasTile(scrollToTileId)) {
                // Javascript struggles to scroll multiple elements at the same time,
                // so we delay scrolling any document on the left and only animate the left document
                setTimeout(() => {
                  row?.tileRowDiv?.scrollIntoView({
                    behavior: this.props.readOnly ? "smooth" : "auto",
                    block: "nearest",
                    inline: "nearest"
                  });
                }, this.props.readOnly ? 100 : 1);
              }
            });
          }
        }
      );
    }
  }

  public componentWillUnmount() {
    this.scrollDisposer?.();
    this.pickUpReactionDisposer?.();
    document.removeEventListener("keydown", this.handlePickUpKeyDown);
    this.domElement?.removeEventListener("mousemove", this.handlePickUpMouseMove);
    this.domElement?.removeEventListener("mouseleave", this.handlePickUpMouseLeave);
  }

  public componentDidUpdate(prevProps: IProps) {
    // recalculate after render
    requestAnimationFrame(() => {
      this.updateVisibleRows();

      const domElement = this.domElement;
      if (!domElement) {
        return;
      }

      // scroll to selected section if it changed
      const {selectedSectionId} = this.props;
      if (selectedSectionId && (selectedSectionId !== prevProps.selectedSectionId)) {
        this.scrollToSection(selectedSectionId);
      }

      // move to current section or top of document when content switches in teacher dashboard
      requestAnimationFrame(() => {
        if (this.props.content !== prevProps.content) {
          if (!this.scrollToSection(this.props.selectedSectionId)) {
            domElement.scrollTo({top: 0});
          }
        }
      });
    });
  }

  public render() {
    const { viaTeacherDashboard } = this.props;
    const { ui, persistentUI, user, appConfig } = this.stores;
    const isChatEnabled = appConfig.showCommentPanelFor(user.type);
    const documentSelectedForComment = isChatEnabled && persistentUI.showChatPanel && ui.selectedTileIds.length === 0
                                          && persistentUI.focusDocument;
    const documentClass = classNames(
      "document-content",
      {"document-content-smooth-scroll" : viaTeacherDashboard, "comment-select" : documentSelectedForComment},
      this.props.readOnly ? "read-only" : "read-write"
    );

    // Reset rowRefs array before rendering
    this.rowRefs = [];

    // We can highlight either the drop location for the current drag/drop operation (in state),
    // or the one set by the toolbar (in the content model).
    const dropRow = this.state.dropRowInfo || this.getDropRowInfoForPendingDropLocation();

    return (
      <DocumentDndContext>
        <DropRowContext.Provider value={dropRow}>
          <ContainerContext.Provider value={{ model: undefined, isLocked: false }}>
            <RowRefsContext.Provider value={{ addRowRef: this.addRowRef }}>
              <div className={documentClass}
                data-testid="document-content"
                data-document-key={this.props.documentId}
                onScroll={this.handleScroll}
                onClick={this.handleClick}
                onDragOver={this.handleDragOver}
                onDragLeave={this.handleDragLeave}
                onDrop={this.handleDrop}
                ref={(elt) => this.domElement = elt}
              >
                {this.renderRows()}
                {this.renderSpacer()}
              </div>
            </RowRefsContext.Provider>
          </ContainerContext.Provider>
        </DropRowContext.Provider>
      </DocumentDndContext>
    );
  }

  // updates the list of all row we can see the bottom of
  private updateVisibleRows = () => {
    const { content } = this.props;

    if (!this.domElement || !content) return;

    const contentBounds = this.domElement.getBoundingClientRect();
    function isElementInViewport(el: Element) {
      const rect = el.getBoundingClientRect();

      return (rect.bottom > contentBounds.top &&
              rect.bottom < contentBounds.bottom);
    }

    // Find the list of top-level rows that are currently visible on the screen
    const visibleRowIds: string[] = [];
    this.rowRefs.forEach((ref) => {
      if (ref?.tileRowDiv && content.rowMap.get(ref.id)) {
        if (isElementInViewport(ref.tileRowDiv)) {
          visibleRowIds.push(ref.id);
        }
      }
    });
    content.setVisibleRows(visibleRowIds);
  };

  private getRowHeight(rowId: string) {
    const { content } = this.props;
    if (!content) return;
    const { rowMap } = content;
    const row = rowMap.get(rowId);
    const { dragResizeRow } = this.state;
    // must match lower-case for ids stored in DataTransfer key
    if (rowId.toLowerCase() !== dragResizeRow?.id) {
      return row?.height;
    }
    const rowHeight = dragResizeRow && (dragResizeRow.domHeight || dragResizeRow.modelHeight);
    if (!dragResizeRow || !rowHeight) return;
    return rowHeight + dragResizeRow.deltaHeight;
  }

  private renderRows() {
    const { content } = this.props;
    if (!content) { return null; }
    return (
      <RowListComponent
        rowListModel={content}
        documentContent={this.domElement}
        context={this.props.context}
        documentId={this.props.documentId}
        docId={content.contentId}
        typeClass={this.props.typeClass}
        scale={this.props.scale}
        readOnly={this.props.readOnly}
      />
    );
  }

  private renderSpacer = () => {
    const { ui } = this.stores;
    const { content, readOnly } = this.props;
    const isEmptyDropTarget = !readOnly && ui.isTilePickedUp && content && content.rowOrder.length === 0;
    const spacerClass = classNames(
      {"spacer" : !readOnly, "playback-spacer": this.props.showPlaybackSpacer},
      {"empty-drop-target": isEmptyDropTarget}
    );
    return <div className={spacerClass} onClick={this.handleClick} />;
  };

  private handleScroll = throttle((e: React.UIEvent<HTMLDivElement>) => {
    const tileApiInterface = this.context;
    const xScroll = this.domElement?.scrollLeft || 0;
    const yScroll = this.domElement?.scrollTop || 0;
    tileApiInterface?.forEach(api => api.handleDocumentScroll?.(xScroll, yScroll));
    this.props.onScroll?.(xScroll, yScroll);
  }, 50);

  private getTileTitle(id: string) {
    const tile = this.props.content?.getTile(id);
    return tile?.computedTitle;
  }

  private handleRequestUniqueTitle = (tileId: string) => {
    const { content } = this.props;
    const tileType = content?.getTile(tileId)?.content.type;
    return tileType && content?.getUniqueTitleForType(tileType);
  };

  private handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const { ui } = this.stores;

    // Handle click-to-place when a tile is picked up
    if (ui.pickedUpTileId && !this.props.readOnly) {
      const dropRowInfo = this.getDropRowInfoFromPoint(e.clientX, e.clientY);
      const isEmptyDocument = this.props.content && this.props.content.rowOrder.length === 0;
      if (dropRowInfo?.rowDropId || (isEmptyDocument && dropRowInfo)) {
        this.handlePickUpPlace(dropRowInfo);
        e.stopPropagation();
        return;
      }
      // Click was in document but not on a valid drop zone — cancel
      ui.clearPickedUpTile();
      this.clearDropRowInfo();
      return;
    }

    // deselect tiles on click on document background
    // click must be on DocumentContent itself, not bubble up from child
    if (e.target === e.currentTarget) {
      ui.setSelectedTile();
    }
  };

  private hasDragType(dataTransfer: DataTransfer, type: string) {
    return dataTransfer.types.findIndex(t => t === type) >= 0;
  }

  private getDragResizeRowInfo(e: React.DragEvent<HTMLDivElement>) {
    const { scale } = this.props;
    const rowId = extractDragResizeRowId(e.dataTransfer);
    const startY = extractDragResizeY(e.dataTransfer);
    const modelHeight = extractDragResizeModelHeight(e.dataTransfer);
    const _domHeight = extractDragResizeDomHeight(e.dataTransfer);
    const domHeight = _domHeight && _domHeight / (scale || 1);
    const deltaHeight = (e.clientY - (startY || 0)) / (scale || 1);
    if (rowId && (deltaHeight != null)) {
      const originalHeight = domHeight || modelHeight;
      const newHeight = originalHeight && originalHeight + deltaHeight;
      return { id: rowId, modelHeight, domHeight, deltaHeight, newHeight };
    }
  }

  private handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    const { content, readOnly } = this.props;
    const { dropRowInfo } = this.state;
    if (!content || readOnly) return;

    const withinDocument = this.hasDragType(e.dataTransfer, dragTileSrcDocId(content.contentId));
    const hasContent = this.hasDragType(e.dataTransfer, kDragTiles);
    const newTileCreation = this.hasDragType(e.dataTransfer, kDragTileCreate);
    if (hasContent || newTileCreation) {
      // Throttle calculation rate slightly to reduce load while dragging
      const lastUpdate = dropRowInfo && dropRowInfo.updateTimestamp ? dropRowInfo.updateTimestamp : 0;
      const now = new Date().getTime();
      if (now - lastUpdate > kDragUpdateInterval) {
        const nextDropRowInfo = this.getDropRowInfo(e);
        this.setState({ dropRowInfo: nextDropRowInfo });
      }
      // indicate we'll accept the drop
      e.dataTransfer.dropEffect = withinDocument && !e.altKey ? "move" : "copy";
      e.preventDefault();
    }
    else if (withinDocument && this.hasDragType(e.dataTransfer, kDragResizeRowId)) {
      const dragResizeRow = this.getDragResizeRowInfo(e);
      if (dragResizeRow?.id && dragResizeRow.newHeight != null) {
        this.setState({ dragResizeRow });
      }
      // indicate we'll accept the drop
      e.dataTransfer.dropEffect = "move";
      e.preventDefault();
    }
  };

  private handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    this.clearDropRowInfo();
  };

  private isPointInRect(x: number, y: number, rect: ClientRect | DOMRect) {
    if ((x == null) || !isFinite(x) || (y == null) || !isFinite(y)) return false;
    return ((x >= rect.left) && (x <= rect.right) && (y >= rect.top) && (y <= rect.bottom));
  }

  // Given the index of an HTML .tile-row on the page, return the info for the row content object
  private getDropInfoForGlobalRowIndex(globalRowIndex: number): IDropRowInfo {
    const { content } = this.props;
    const row = content?.allRows[globalRowIndex];
    const rowList = row && content?.getRowListForRow(row.id);
    const rowIndex = row && rowList?.getRowIndex(row.id);
    if (!row || rowIndex === undefined || rowIndex === -1) return { rowInsertIndex: 0 };
    return { rowDropId: row.id, rowInsertIndex: rowIndex };
  }

  // Determine whether a point is over a drop zone, and if so, which one
  private getDropRowInfoFromPoint = (clientX: number, clientY: number): IDropRowInfo | undefined => {
    const { content } = this.props;
    if (!this.domElement || !content) {
      return { rowInsertIndex: content ? content.rowOrder.length : 0 };
    }

    let dropInfo: IDropRowInfo = { rowInsertIndex: 0 };
    // This includes both "main" rows and rows nested inside question tiles
    const rowElements = this.domElement.getElementsByClassName("tile-row");

    // Find the last "main" row
    let lastRowIndex = -1, lastRowBottom = 0;
    for (let i = 0; i < rowElements.length; ++i) {
      const rowElt = rowElements[i];
      const rowBounds = rowElt.getBoundingClientRect();
      if (rowBounds.bottom > lastRowBottom) {
        lastRowIndex = i;
        lastRowBottom = rowBounds.bottom;
      }
    }

    for (let i = 0; i < rowElements.length; ++i) {
      const rowElt = rowElements[i];
      const rowBounds = rowElt.getBoundingClientRect();
      if (this.isPointInRect(clientX, clientY, rowBounds) ||
          // below the last row - pretend we are in the last row
          ((i === lastRowIndex) && (clientY > rowBounds.bottom))) {
        dropInfo = this.getDropInfoForGlobalRowIndex(i);
        if (!dropInfo.rowDropId) return;
        const row = content?.getRowRecursive(dropInfo.rowDropId);
        if (row?.isFixedPositionRow(content?.tileMap)) {
          // Cannot drop alongside or above a fixed position row
          dropInfo.rowDropLocation = "bottom";
          dropInfo.rowInsertIndex = i + 1;
        } else {
          const dropOffsetTop = Math.abs(clientY - rowBounds.top);
          const dropOffsetBottom = Math.abs(rowBounds.bottom - clientY);

          // Uniform tile-boundary detection for all rows with tiles.
          // For N tiles there are N+1 boundaries: left edge, between each pair, right edge.
          // If the cursor is near any boundary, it's a side drop with a tileInsertIndex.
          const tileElements = rowElt.querySelectorAll(':scope > .tool-tile');
          let sideDropDetected = false;

          if (tileElements.length > 0) {
            // Scale threshold with tile size: 15% of average tile width, minimum 25px
            const avgTileWidth = (tileElements[tileElements.length - 1].getBoundingClientRect().right
              - tileElements[0].getBoundingClientRect().left) / tileElements.length;
            const kSideDropThreshold = Math.max(25, avgTileWidth * 0.15);
            const boundaries: number[] = [];
            // First boundary: left edge of the first tile
            boundaries.push(tileElements[0].getBoundingClientRect().left);
            // Interior boundaries: midpoint between adjacent tiles
            for (let t = 0; t < tileElements.length - 1; t++) {
              const leftRight = tileElements[t].getBoundingClientRect().right;
              const rightLeft = tileElements[t + 1].getBoundingClientRect().left;
              boundaries.push((leftRight + rightLeft) / 2);
            }
            // Last boundary: right edge of the last tile
            boundaries.push(tileElements[tileElements.length - 1].getBoundingClientRect().right);

            // Find the nearest boundary
            let nearestIndex = 0;
            let nearestDist = Infinity;
            for (let b = 0; b < boundaries.length; b++) {
              const dist = Math.abs(clientX - boundaries[b]);
              if (dist < nearestDist) {
                nearestDist = dist;
                nearestIndex = b;
              }
            }

            if (nearestDist < kSideDropThreshold) {
              dropInfo.rowDropLocation = "left";
              dropInfo.tileInsertIndex = nearestIndex;
              sideDropDetected = true;
            }
          }

          if (!sideDropDetected) {
            if (dropOffsetTop < dropOffsetBottom) {
              dropInfo.rowDropLocation = "top";
            }
            else {
              dropInfo.rowDropLocation = "bottom";
              dropInfo.rowInsertIndex = i + 1;
            }
          }
        }
      }
    }
    dropInfo.updateTimestamp = new Date().getTime();
    return dropInfo;
  };

  // Wrapper for drag events
  private getDropRowInfo = (e: React.DragEvent<HTMLDivElement>) => {
    return this.getDropRowInfoFromPoint(e.clientX, e.clientY);
  };

  private clearDropRowInfo() {
    if (this.state.dropRowInfo) {
      this.setState({ dropRowInfo: undefined });
    }
  }

  // --- Click-to-pick: mousemove/keyboard highlights drop zones, click/Enter places the tile ---

  private handlePickUpMouseLeave = () => {
    this.clearDropRowInfo();
  };

  // When the mouse moves during pick-up, clear keyboard focus so mouse takes over
  private handlePickUpMouseMove = (e: MouseEvent) => {
    const { ui } = this.stores;
    if (ui.focusedDropZoneIndex !== undefined) {
      ui.setFocusedDropZoneIndex(undefined);
    }
    const { dropRowInfo } = this.state;
    const lastUpdate = dropRowInfo?.updateTimestamp ?? 0;
    const now = Date.now();
    if (now - lastUpdate > kDragUpdateInterval) {
      const nextDropRowInfo = this.getDropRowInfoFromPoint(e.clientX, e.clientY);
      this.setState({ dropRowInfo: nextDropRowInfo });
    }
  };

  // --- Keyboard navigation of drop zones during pick-up ---

  // Compute an ordered list of valid drop zones from the document's rows.
  // Each zone has a location (top/left/right/bottom), the rowId, and an IDropRowInfo for placement.
  private getDropZoneList() {
    const { content } = this.props;
    if (!content) return [];

    // "right" is used by internal callers (e.g. userAddTile) but not by the UI's
    // getDropRowInfoFromPoint, which uses "left" with tileInsertIndex for all side drops.
    type DropZoneLocation = "top" | "left" | "bottom";
    interface IDropZone {
      rowIndex: number;
      rowId: string;
      location: DropZoneLocation;
      dropRowInfo: IDropRowInfo;
    }

    const zones: IDropZone[] = [];
    const allRows = content.allRows;

    allRows.forEach((row, i) => {
      // Use getDropInfoForGlobalRowIndex to get the correct local rowInsertIndex
      // within the containing RowList (not the global allRows index).
      const baseInfo = this.getDropInfoForGlobalRowIndex(i);
      if (!baseInfo.rowDropId) return;

      const isSectionHeader = row.isSectionHeader;
      const isFixed = row.isFixedPositionRow(content.tileMap);

      if (isFixed) {
        // Fixed position rows only allow insertion below
        zones.push({
          rowIndex: i, rowId: row.id, location: "bottom",
          dropRowInfo: { ...baseInfo, rowDropLocation: "bottom", rowInsertIndex: baseInfo.rowInsertIndex + 1 }
        });
        return;
      }

      // Top zone: available unless it's a section header at the very first row
      if (!isSectionHeader || i > 0) {
        zones.push({
          rowIndex: i, rowId: row.id, location: "top",
          dropRowInfo: { ...baseInfo, rowDropLocation: "top" }
        });
      }

      // Tile-boundary side zones: N+1 zones for N tiles in the row.
      // Filter to renderable tiles to match what tile-row.tsx renders.
      if (!isSectionHeader && row.tileCount > 0) {
        const renderableTileCount = row.tiles.filter(t => {
          const tile = content.tileMap.get(t.tileId);
          return tile && (!tile.display || this.stores.isShowingTeacherContent);
        }).length;
        for (let t = 0; t <= renderableTileCount; t++) {
          zones.push({
            rowIndex: i, rowId: row.id, location: "left",
            dropRowInfo: { ...baseInfo, rowDropLocation: "left", tileInsertIndex: t }
          });
        }
      }

      // Bottom zone: always available
      zones.push({
        rowIndex: i, rowId: row.id, location: "bottom",
        dropRowInfo: { ...baseInfo, rowDropLocation: "bottom", rowInsertIndex: baseInfo.rowInsertIndex + 1 }
      });
    });

    return zones;
  }

  private setFocusedZone(zones: ReturnType<typeof this.getDropZoneList>, index: number) {
    const { ui } = this.stores;
    ui.setFocusedDropZoneIndex(index);
    this.setState({ dropRowInfo: zones[index].dropRowInfo });
  }

  private handlePickUpKeyDown = (e: KeyboardEvent) => {
    const { ui } = this.stores;
    if (!ui.pickedUpTileId) return;

    // Don't intercept keys when focus is on interactive elements (e.g. the delete button)
    const active = document.activeElement as HTMLElement | null;
    if (active?.closest(".delete-button, button, input, select, textarea")) return;

    const zones = this.getDropZoneList();
    const { content } = this.props;
    const isEmptyDocument = content && content.rowOrder.length === 0;

    // Empty document: Enter places the tile at index 0, arrow keys are no-ops
    if (zones.length === 0) {
      if (isEmptyDocument && e.key === "Enter") {
        e.preventDefault();
        this.handlePickUpPlace({ rowInsertIndex: 0 });
      }
      return;
    }

    const currentIndex = ui.focusedDropZoneIndex;

    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight": {
        e.preventDefault();
        if (currentIndex === undefined) {
          this.setFocusedZone(zones, 0);
        } else if (currentIndex < zones.length - 1) {
          this.setFocusedZone(zones, currentIndex + 1);
        }
        break;
      }
      case "ArrowUp":
      case "ArrowLeft": {
        e.preventDefault();
        if (currentIndex === undefined) {
          this.setFocusedZone(zones, zones.length - 1);
        } else if (currentIndex > 0) {
          this.setFocusedZone(zones, currentIndex - 1);
        }
        break;
      }
      case "Enter": {
        e.preventDefault();
        if (currentIndex !== undefined && currentIndex < zones.length) {
          this.handlePickUpPlace(zones[currentIndex].dropRowInfo);
        }
        break;
      }
      case "Tab": {
        // Move focus to the delete button
        e.preventDefault();
        const deleteButton = this.domElement?.closest(".problem-panel")?.querySelector<HTMLElement>(".delete-button");
        if (deleteButton) {
          deleteButton.focus();
        }
        break;
      }
      case "Escape": {
        e.preventDefault();
        ui.clearPickedUpTile();
        break;
      }
    }
  };

  private findContentOfTile(tileId: string) {
    return this.stores.findContentOfTile(tileId);
  }

  private handlePickUpPlace = (dropRowInfo: IDropRowInfo) => {
    const { content } = this.props;
    const { ui } = this.stores;
    const tileId = ui.pickedUpTileId;

    if (!content || !tileId) return;

    // Check local document first: template tile IDs (e.g. TemplateTextTileQ1_1) are reused across
    // documents, so a global search may find the wrong document. If the tile is in the local
    // document's tileMap, it's a same-document move.
    const isLocalTile = content.tileMap.has(tileId);
    const sourceContent = isLocalTile ? content : this.findContentOfTile(tileId);
    if (!sourceContent) {
      ui.clearPickedUpTile();
      return;
    }

    const isSameDocument = isLocalTile || sourceContent.contentId === content.contentId;

    if (isSameDocument) {
      // Move within the same document
      const dragTileItems = sourceContent.getDragTileItems([tileId]);
      const tilesToMove = sourceContent.removeEmbeddedTilesFromDragTiles(dragTileItems);
      content.userMoveTiles(tilesToMove, dropRowInfo);
    } else {
      // Copy from another document (e.g. resources pane → workspace)
      const dragTiles = sourceContent.getDragTiles([tileId]);
      content.handleDragCopyTiles(dragTiles, dropRowInfo);
    }

    ui.clearPickedUpTile();
    this.clearDropRowInfo();
  };

  private handleRowResizeDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const { content } = this.props;
    const dragResizeRow = this.getDragResizeRowInfo(e);
    if (content && dragResizeRow?.id && dragResizeRow.newHeight != null) {
      const row = content.getRowRecursive(dragResizeRow.id);
      row?.setRowHeight(dragResizeRow.newHeight);
      this.setState({ dragResizeRow: undefined });
    }
  };

  private handleMoveTilesDrop = (e: React.DragEvent<HTMLDivElement>, dragTilesData: IDragTilesData) => {
    const dropRowInfo = this.getDropRowInfo(e);
    const content = this.props.content;
    if (!dropRowInfo || !content) return;
    content.userMoveTiles(content.removeEmbeddedTilesFromDragTiles(dragTilesData.tiles), dropRowInfo);
  };

  private handleCopyTilesDrop = (e: React.DragEvent<HTMLDivElement>, dragTiles: IDragTilesData) => {
    const dropRowInfo = this.getDropRowInfo(e);
    if (!dropRowInfo) return;
    this.props.content?.handleDragCopyTiles(dragTiles, dropRowInfo);
  };

  private handleInsertNewTile = (e: React.DragEvent<HTMLDivElement>) => {
    const { content } = this.props;
    const { ui } = this.stores;

    const createTileInfoStr = e.dataTransfer.getData(kDragTileCreate);
    const createTileInfo = safeJsonParse<IDragToolCreateInfo>(createTileInfoStr);
    if (!content || !createTileInfo) return;

    const { toolId, title } = createTileInfo;
    const insertRowInfo = this.getDropRowInfo(e);
    const rowTile = content.userAddTile(toolId, {title, insertRowInfo});

    if (rowTile?.tileId) {
      ui.setSelectedTileId(rowTile.tileId);
    }
  };

  private handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const { content, readOnly } = this.props;
    content?.showPendingInsertHighlight(false);

    if (!e.dataTransfer || !content || readOnly) return;

    logDataTransfer(e.dataTransfer);

    if (this.hasDragType(e.dataTransfer, kDragResizeRowId)) {
      this.handleRowResizeDrop(e);
      return;
    }

    e.preventDefault();

    const dragTilesJson = e.dataTransfer.getData(kDragTiles);
    if (dragTilesJson) {
      try {
        const dragTiles: IDragTilesData = JSON.parse(dragTilesJson);
        if ((dragTiles.sourceDocId === content.contentId) && !e.altKey) {
          this.handleMoveTilesDrop(e, dragTiles);
        }
        else {
          this.handleCopyTilesDrop(e, dragTiles);
        }
      } catch (ex) {
        console.error(ex);
      }

      this.clearDropRowInfo();
      e.stopPropagation(); // Prevent other drop actions, like opening the document the tiles came from
      return;
    }

    const dragCreateTileType = e.dataTransfer.getData(kDragTileCreate);

    // handle drop to create new tile
    if (dragCreateTileType) {
      this.handleInsertNewTile(e);
    }

    this.clearDropRowInfo();
  };

  private getDropRowInfoForPendingDropLocation(): IDropRowInfo | undefined {
    const { content } = this.props;
    const rowId = content?.highlightPendingDropLocation;
    if (!rowId) return;
    const rowIndex = content.getRowListForRow(rowId)?.getRowIndex(rowId);
    if (rowIndex < 0) return;
    return {
      rowDropId: rowId,
      rowDropLocation: "bottom",
      rowInsertIndex: rowIndex + 1
    };
  }

  private scrollToSection(sectionId: string | null | undefined ) {
    if (!sectionId || !this.domElement) {
      return false;
    }

    const sectionElementTop = this.findSectionElementTop(this.domElement, sectionId);
    if (sectionElementTop !== undefined) {
      this.domElement.scrollTo({top: sectionElementTop});
      return true;
    }
    return false;
  }

  private findSectionElementTop(parent: HTMLElement, sectionId: string, top = 0): number | undefined {
    for (let i = 0; i < parent.childNodes.length; i++) {
      const child = parent.childNodes.item(i) as HTMLElement;
      if (child.nodeType === 1) { // 1 is element
        if (child.id === `section_${sectionId}`) {
          return top + child.offsetTop;
        }
        if (child.childNodes) {
          const sectionElementTop = this.findSectionElementTop(child, sectionId, top);
          if (sectionElementTop !== undefined) {
            return sectionElementTop;
          }
        }
        top += child.clientHeight;
      }
    }
  }

}
